import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import '../styles/general.css';

/**
 * Modal
 * Generic overlay dialog. Closes on overlay click or the close (×) button.
 * Renders nothing when isOpen is false.
 *
 * Rendered via a portal into document.body: modals are frequently opened
 * from inside elements that establish their own stacking context (e.g. a
 * sticky table cell with its own z-index), which traps a normally-positioned
 * overlay behind sibling elements no matter how high its own z-index is.
 * Portaling out of the DOM tree sidesteps that entirely.
 */
const Modal = ({ isOpen, onClose, title, children }) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content" role="dialog" aria-modal="true" aria-label={title}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            &times;
          </button>
        </div>
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;
